import { useCallback, useMemo } from 'react';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';

import { TASKS_CONFIGS } from '../../../../constants';
import { useGetAction } from '../../../../hooks/useNextSteps/useTasks/useGetAction';
import type { Task } from '../../../../types';
import type { PetitionTaskNode } from '../useDynamicTasks/taskShapes';

type ExistingTask = Readonly<{
	id: Task['id'];
	name: Task['name'];
	status: Task['status'];
}>;

type CtaAction = () => Promise<boolean>;

function hasDynamicTaskCta(featureKey: string | null): featureKey is string {
	return featureKey != null && (Object.hasOwn(TASKS_CONFIGS, featureKey) || featureKey === 'download_signatures');
}

export function useDynamicTaskAction(): (
	featureKey: string | null,
	taskId: Task['id'],
	taskStatus: Task['status'],
) => CtaAction | undefined {
	const { data } = useStarterDashboardV2PageData();
	const getAction = useGetAction();

	const existingTasksByName = useMemo<ReadonlyMap<string, ExistingTask>>(() => {
		const { activeTasks } = data.petition;
		if (activeTasks.__typename !== 'PetitionTasksConnection') return new Map();
		return new Map(activeTasks.nodes.map((task: PetitionTaskNode) => [task.name, task]));
	}, [data.petition]);

	return useCallback(
		(featureKey: string | null, taskId: Task['id'], taskStatus: Task['status']) => {
			if (!hasDynamicTaskCta(featureKey)) return undefined;
			const existingTask = existingTasksByName.get(featureKey);
			return getAction({
				id: existingTask?.id ?? taskId,
				name: existingTask?.name ?? featureKey,
				status: existingTask?.status ?? taskStatus,
				version: 'new_ai_03_2026',
			});
		},
		[getAction, existingTasksByName],
	);
}
